import { type FC } from 'react';

import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';

import { BlueprintIcon } from 'components/icons/blueprint/BlueprintIcon';

import { DecompositionTree } from '../../components/DecompositionTree';
import { DECOMPOSITION_TREE_TEST_ID } from '../../testIds';
import type { ItemMeta, RichTreeItem } from '../../types';

export type MoveNodeDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  items: RichTreeItem[];
  itemMetaMap: Record<string, ItemMeta>;
  onShowMore: (parentId: string) => void;
  nodeIcon?: string;
  loadingParentIds?: Set<string>;
  moveNodeId: string;
  moveNodeMeta: ItemMeta;
  selectedParentId: string | null | undefined;
  disabledParentTreeId: string | null;
  isMoveToRootDisabled: boolean;
  canSave: boolean;
  isStale?: boolean;
  isSaving?: boolean;
  onSelectParent: (treeId: string) => void;
  onSelectRoot: () => void;
  expandedItems: string[];
  onExpandedItemsChange: (event: React.SyntheticEvent | null, items: string[]) => void;
  hasMoreRoots?: boolean;
  isFetchingMoreRoots?: boolean;
  onLoadMoreRoots?: () => void;
};

export const MoveNodeDialog: FC<MoveNodeDialogProps> = ({
  open,
  onClose,
  onSave,
  items,
  itemMetaMap,
  onShowMore,
  nodeIcon,
  loadingParentIds,
  moveNodeId,
  moveNodeMeta,
  selectedParentId,
  disabledParentTreeId,
  isMoveToRootDisabled,
  canSave,
  isStale = false,
  isSaving = false,
  onSelectParent,
  onSelectRoot,
  expandedItems,
  onExpandedItemsChange,
  hasMoreRoots,
  isFetchingMoreRoots,
  onLoadMoreRoots
}) => {
  const nodeLabel = [moveNodeMeta.autoId, moveNodeMeta.title || moveNodeMeta.titlePlaceholder]
    .filter(Boolean)
    .join(' - ');
  const title = nodeLabel ? `Move ${nodeLabel}` : 'Move node';
  const isMoveToRootDisabledState = isMoveToRootDisabled || selectedParentId === null;

  const rootLevelButton = (
    <Button
      size='small'
      variant='text'
      disabled={isMoveToRootDisabledState}
      onClick={onSelectRoot}
      data-testid={DECOMPOSITION_TREE_TEST_ID.moveToRootButton}
      startIcon={nodeIcon ? <BlueprintIcon name={nodeIcon} fontSize='small' /> : undefined}
    >
      Move to root
    </Button>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth data-testid={DECOMPOSITION_TREE_TEST_ID.moveDialog}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '80vh', overflow: 'auto', p: 0, position: 'relative' }}>
        {isStale && (
          <Box sx={{ m: 2, mb: 0 }}>
            <Alert severity='warning'>This node was moved by another user. Close the dialog to continue.</Alert>
          </Box>
        )}
        <Box sx={{ position: 'relative', ...((isStale || isSaving) && { pointerEvents: 'none', opacity: 0.5 }) }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
              py: 1
            }}
          >
            {isMoveToRootDisabled ? (
              <Tooltip title='This node is already at root level'>
                <span>{rootLevelButton}</span>
              </Tooltip>
            ) : (
              rootLevelButton
            )}
          </Box>
          <Box sx={{ p: 2 }}>
            <DecompositionTree
              items={items}
              itemMetaMap={itemMetaMap}
              onShowMore={onShowMore}
              expandedItems={expandedItems}
              onExpandedItemsChange={onExpandedItemsChange}
              nodeIcon={nodeIcon}
              loadingParentIds={loadingParentIds}
              isMoveMode
              selectedMoveNodeId={moveNodeId}
              selectedParentId={selectedParentId}
              onSelectParent={onSelectParent}
              disabledParentTreeId={disabledParentTreeId}
              testIdScope='move'
            />
            {hasMoreRoots && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <LoadingButton
                  size='small'
                  variant='text'
                  disabled={isSaving || isStale}
                  data-testid={DECOMPOSITION_TREE_TEST_ID.moveLoadMoreButton}
                  onClick={onLoadMoreRoots}
                  loading={isFetchingMoreRoots}
                >
                  Load more
                </LoadingButton>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSaving} onClick={onClose} data-testid={DECOMPOSITION_TREE_TEST_ID.moveCancelButton}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          onClick={onSave}
          disabled={!canSave}
          data-testid={DECOMPOSITION_TREE_TEST_ID.moveSaveButton}
          loading={isSaving}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
